import React from 'react';

import { PenTool, ArrowRight, ArrowLeft, Loader2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DatePickerFr } from '../../components/ui/DatePickerFr';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';

import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';

import { toast } from 'sonner';

import { formatCodeNAF } from '../../utils/codeNaf';

import DocumentMaitreAdhesionFTPE from '../../components/DocumentMaitreAdhesionFTPE';

import PhoneInputWithFlagSVG from '../../components/PhoneInputWithFlagSVG';
import SIRETInput from '../../components/SIRETInput';
import TerritorySelector from '../../components/TerritorySelector';

import { PLAN_FEATURES, TYPE_ADHESION_OPTIONS } from './ftpeConstants';

export default function FtpeSteps123({ ctx }) {
  const { currentStep, formData, handlePreviewBulletin, handleSubmitForm, i: i, loading, previewingBulletin, required: required, setCurrentStep, text: text, updateFormData } = ctx;
  return (
    <>
        {/* ÉTAPE 1: Document Maître d'Adhésion */}
        {currentStep === 1 && (
          <div className="-mx-6 -mt-6">
            <DocumentMaitreAdhesionFTPE 
              onAccept={() => setCurrentStep(2)}
              showSignatureButton={true}
              cotisation={200}
            />
          </div>
        )}

        {/* ÉTAPE 2: Présentation des avantages */}
        {currentStep === 2 && (
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <Badge className="bg-amber-600 text-white px-4 py-1 mb-4 font-semibold">
                  COTISATION ANNUELLE
                </Badge>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Rejoignez la FTPE Outremer
                </h2>
                <p className="text-gray-600">
                  Fédération des Très Petites Entreprises Outremer
                </p>
              </div>

              <div className="flex justify-center mb-8">
                <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border-2 border-amber-300">
                  <span className="text-5xl font-bold text-amber-600">200 €</span>
                  <span className="text-gray-600 text-lg ml-2">/ an</span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {PLAN_FEATURES.map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-gray-700">{feature.text}</span>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 bg-gray-100 rounded-lg text-sm text-gray-600 mb-8">
                <p>
                  <strong>Note :</strong> L&apos;adhésion implique l&apos;acceptation des statuts, 
                  du règlement intérieur et de la charte d&apos;éthique.
                </p>
              </div>

              <div className="flex gap-4">
                <Button 
                  variant="outline"
                  onClick={() => setCurrentStep(1)}
                  className="flex-1 h-14 text-lg"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Revoir le document
                </Button>
                <Button 
                  onClick={() => setCurrentStep(3)}
                  className="flex-1 h-14 text-lg bg-black hover:bg-gray-800"
                >
                  Continuer l&apos;adhésion
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ÉTAPE 3: Formulaire */}
        {currentStep === 3 && (
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle>Informations de l&apos;entreprise</CardTitle>
              <CardDescription>Renseignez les informations de votre entreprise</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Section 1: Identification */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-l-4 border-amber-500 pl-3">
                  1. Identification de l&apos;entreprise
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Raison sociale *</Label>
                    <Input 
                      value={formData.raison_sociale}
                      onChange={(e) => updateFormData('raison_sociale', e.target.value)}
                      placeholder="Nom de l&apos;entreprise"
                    />
                  </div>
                  <div>
                    <Label>Nom commercial</Label>
                    <Input 
                      value={formData.nom_commercial}
                      onChange={(e) => updateFormData('nom_commercial', e.target.value)}
                      placeholder="Si différent"
                    />
                  </div>
                  <div>
                    <Label>Forme juridique</Label>
                    <Input 
                      value={formData.forme_juridique}
                      onChange={(e) => updateFormData('forme_juridique', e.target.value)}
                      placeholder="SARL, SAS, EI..."
                    />
                  </div>
                  <div>
                    <SIRETInput
                      value={formData.siret}
                      onChange={(val) => updateFormData('siret', val)}
                      required={true}
                      onAutoComplete={(companyData) => {
                        // Auto-remplir les champs si données INSEE disponibles
                        if (companyData.raison_sociale) updateFormData('raison_sociale', companyData.raison_sociale);
                        if (companyData.code_naf) updateFormData('code_ape', formatCodeNAF(companyData.code_naf));
                        if (companyData.forme_juridique) updateFormData('forme_juridique', companyData.forme_juridique);
                        if (companyData.adresse) updateFormData('adresse', companyData.adresse);
                        if (companyData.code_postal) updateFormData('code_postal', companyData.code_postal);
                        if (companyData.ville) updateFormData('ville', companyData.ville);
                        if (companyData.date_creation) updateFormData('date_creation', companyData.date_creation);
                        toast.success('Données entreprise récupérées');
                      }}
                    />
                  </div>
                  <div>
                    <Label>Code APE</Label>
                    <Input 
                      value={formData.code_ape}
                      onChange={(e) => updateFormData('code_ape', e.target.value.toUpperCase())}
                      onBlur={(e) => updateFormData('code_ape', formatCodeNAF(e.target.value))}
                      className="font-mono"
                      placeholder="Ex: 43.99C"
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <Label>Date de création</Label>
                    <DatePickerFr
                      className="w-full"
                      value={formData.date_creation}
                      onChange={(v) => updateFormData('date_creation', v)}
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Coordonnées */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-l-4 border-amber-500 pl-3">
                  2. Coordonnées
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Adresse du siège social *</Label>
                    <Input 
                      value={formData.adresse}
                      onChange={(e) => updateFormData('adresse', e.target.value)}
                      placeholder="Adresse complète"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TerritorySelector
                      value={formData.territoire}
                      onChange={(val) => updateFormData('territoire', val)}
                      label="Territoire"
                      required
                      showDetails={true}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <PhoneInputWithFlagSVG 
                      value={formData.telephone}
                      onChange={(val) => updateFormData('telephone', val)}
                      label="Téléphone"
                      required
                      territory={formData.territoire}
                      defaultCountry="GP"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder="contact@entreprise.fr"
                    />
                  </div>
                  <div>
                    <Label>Site internet</Label>
                    <Input 
                      value={formData.site_web}
                      onChange={(e) => updateFormData('site_web', e.target.value)}
                      placeholder="www.entreprise.fr"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Représentant */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-l-4 border-amber-500 pl-3">
                  3. Représentant légal
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom et prénom *</Label>
                    <Input 
                      value={formData.representant_nom}
                      onChange={(e) => updateFormData('representant_nom', e.target.value)}
                      placeholder="Prénom NOM"
                    />
                  </div>
                  <div>
                    <Label>Fonction</Label>
                    <Input 
                      value={formData.representant_fonction}
                      onChange={(e) => updateFormData('representant_fonction', e.target.value)}
                      placeholder="Gérant, Président..."
                    />
                  </div>
                  <div>
                    <Label>Email direct</Label>
                    <Input 
                      type="email"
                      value={formData.representant_email}
                      onChange={(e) => updateFormData('representant_email', e.target.value)}
                    />
                  </div>
                  <div>
                    <PhoneInputWithFlagSVG 
                      value={formData.representant_telephone}
                      onChange={(val) => updateFormData('representant_telephone', val)}
                      label="Téléphone"
                      territory={formData.territoire}
                      defaultCountry="GP"
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Type d&apos;adhésion */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-l-4 border-amber-500 pl-3">
                  4. Type d&apos;adhésion
                </h3>
                <RadioGroup 
                  value={formData.type_adhesion}
                  onValueChange={(v) => updateFormData('type_adhesion', v)}
                  className="space-y-2"
                >
                  {TYPE_ADHESION_OPTIONS.map(opt => (
                    <div key={opt.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                      <RadioGroupItem value={opt.value} id={opt.value} />
                      <Label htmlFor={opt.value} className="cursor-pointer flex-1">{opt.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Section 5: RGPD */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 border-l-4 border-amber-500 pl-3">
                  5. Autorisation de communication (RGPD)
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="rgpd1"
                      checked={formData.autorisation_communication}
                      onCheckedChange={(c) => updateFormData('autorisation_communication', c)}
                    />
                    <Label htmlFor="rgpd1" className="text-sm leading-relaxed cursor-pointer">
                      J&apos;autorise la FTPE Outremer à utiliser mes coordonnées professionnelles dans le cadre strict de ses actions fédératives.
                    </Label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="rgpd2"
                      checked={formData.reception_informations}
                      onCheckedChange={(c) => updateFormData('reception_informations', c)}
                    />
                    <Label htmlFor="rgpd2" className="text-sm leading-relaxed cursor-pointer">
                      Je souhaite recevoir les informations, actualités et communications de la FTPE Outremer.
                    </Label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-6">
                <Button 
                  variant="outline"
                  onClick={() => setCurrentStep(2)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
                <Button 
                  variant="outline"
                  onClick={handlePreviewBulletin}
                  disabled={previewingBulletin || !formData.raison_sociale}
                  className="flex-1"
                >
                  {previewingBulletin ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Prévisualiser le bulletin
                </Button>
                <Button 
                  onClick={handleSubmitForm}
                  disabled={loading}
                  className="flex-1 bg-black hover:bg-gray-800"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <PenTool className="w-4 h-4 mr-2" />
                  )}
                  Générer et signer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

    </>
  );
}
